import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';

// Custom
import Loader from '@/ui/Loader/Loader';
import Permissions from '@/lib/permissions';
import UserContext, { UserContextType } from '@/context/UserContext/UserContext';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import UpitnikForm from './UpitnikForm';

// Services
import { upitnikService, Upitnik as UpitnikData } from '@/services/upitnikService';

type Screen = 'loading' | 'form' | 'hvala' | 'zatvoren' | 'nevazeci' | 'limit' | 'prijavljen';

type UpitnikProps = {
  token: string;
};

const Upitnik = ({ token }: UpitnikProps) => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext) as UserContextType;
  const { showNotification } = useContext(SnackbarContext);

  const [screen, setScreen] = useState<Screen>('loading');
  const [upitnik, setUpitnik] = useState<UpitnikData | null>(null);
  const [formKey, setFormKey] = useState(0);

  // logged-in staff without the public permission would get a 403, and a 403 logs them out
  const canFill = userContext.hasAnyPermission([Permissions.GrobljaUpitnik]);

  useEffect(() => {
    if (!canFill) {
      setScreen('prijavljen');
      return;
    }
    upitnikService.getUpitnik(token).then((resp) => {
      const data = resp.success ? (resp.data as UpitnikData) : null;
      if (data?.mode === 'preusmjeren' && data.url) {
        // replace, so the back button doesn't land on the redirect again; the loader stays until the browser leaves
        window.location.replace(data.url);
        return;
      }
      setUpitnik(data);
      setScreen(data?.mode && data.mode !== 'preusmjeren' ? data.mode : 'nevazeci');
    });
  }, [token, canFill]);

  const handleDone = (privitciOk: boolean) => {
    if (!privitciOk) {
      showNotification(t('groblja:upitnik.privitci_greska'), 'warning');
    }
    setScreen('hvala');
  };

  const handleError = (message?: string) => {
    if (message === 'LIMIT') setScreen('limit');
    else if (message === 'ZATVOREN') setScreen('zatvoren');
    else if (message === 'NEVAZECI') setScreen('nevazeci');
    else showNotification(t('groblja:upitnik.greska'), 'error');
  };

  const handleAgain = () => {
    setFormKey((k) => k + 1);
    setScreen('form');
  };

  if (screen === 'loading') {
    return <Loader open />;
  }

  if (screen === 'form' && upitnik) {
    return <UpitnikForm key={formKey} token={token} upitnik={upitnik} onDone={handleDone} onError={handleError} />;
  }

  const messages: { [key in Exclude<Screen, 'loading' | 'form'>]: string } = {
    hvala: 'groblja:upitnik.hvala',
    zatvoren: 'groblja:upitnik.zatvoren',
    nevazeci: 'groblja:upitnik.nevazeci',
    limit: 'groblja:upitnik.limit',
    prijavljen: 'groblja:upitnik.prijavljen',
  };

  return (
    <Card>
      <CardContent>
        {screen === 'hvala' ? (
          <Typography variant="h5" component="h1" gutterBottom>
            {t('groblja:upitnik.hvala_naslov')}
          </Typography>
        ) : null}
        <Typography variant="body1">{t(messages[screen as keyof typeof messages])}</Typography>
      </CardContent>
      {screen === 'hvala' ? (
        <CardActions>
          <Button variant="contained" color="primary" onClick={handleAgain}>
            {t('groblja:upitnik.ispuni_ponovno')}
          </Button>
        </CardActions>
      ) : null}
    </Card>
  );
};

export default Upitnik;
